import { Link } from 'react-router-dom';

const useCases = [
  {
    icon: 'ri-home-gear-line',
    title: 'Gebäudeautomation & Smart Building',
    description:
      'Wir planen und realisieren vollständige Loxone-Installationen für Gewerbe- und Industriegebäude. Beleuchtung, Beschattung, Heizung, Lüftung und Klimatisierung werden intelligent vernetzt und zentral gesteuert.',
    tags: ['Loxone Miniserver', 'KNX-Integration', 'HVAC', 'Beleuchtungssteuerung'],
  },
  {
    icon: 'ri-shield-keyhole-line',
    title: 'Sicherheitstechnik & Zutrittskontrolle',
    description:
      'Intelligente Sicherheitslösungen mit Loxone: Alarmanlage, Bewegungsmelder, Videoüberwachung und Zutrittskontrolle – alles in einem System integriert und per App steuerbar.',
    tags: ['Alarmanlage', 'Zutrittskontrolle', 'Bewegungsmelder', 'Kameraintegration'],
  },
  {
    icon: 'ri-sun-line',
    title: 'Energie- & Lichtmanagement',
    description:
      'Automatische Beschattungssteuerung, tageslichtabhängige Beleuchtung und Energiemonitoring senken Betriebskosten und steigern den Komfort. Loxone erkennt Anwesenheit und passt alle Parameter automatisch an.',
    tags: ['Energiemonitoring', 'Beschattung', 'DALI', 'Präsenzsteuerung'],
  },
  {
    icon: 'ri-wifi-line',
    title: 'Netzwerk & Systemintegration',
    description:
      'Nahtlose Integration von Loxone in bestehende Netzwerkinfrastrukturen. Anbindung an Drittanbieter über HTTP, MQTT, KNX und Modbus – für maximale Flexibilität in komplexen Gebäudeumgebungen.',
    tags: ['MQTT', 'KNX', 'Modbus', 'HTTP-API'],
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Visualisierung & App-Steuerung',
    description:
      'Intuitive Bedienung über die Loxone App – für iOS, Android und Webbrowser. Individuelle Visualisierungen, Szenensteuerung und Fernzugriff für Facility Management und Wartung.',
    tags: ['Loxone App', 'Webinterface', 'Szenensteuerung', 'Fernzugriff'],
  },
  {
    icon: 'ri-leaf-line',
    title: 'Photovoltaik & Ladeinfrastruktur',
    description:
      'Loxone steuert PV-Anlagen, Batteriespeicher und Wallboxen intelligent – Eigenverbrauch wird maximiert, Ladezeiten werden automatisch auf günstige Tarife und Sonnenstunden optimiert.',
    tags: ['PV-Integration', 'Batteriespeicher', 'Wallbox', 'Eigenverbrauch'],
  },
];

const highlights = [
  { value: 'Miniserver', label: 'Loxone Zentrale' },
  { value: 'KNX / MQTT', label: 'Protokolle' },
  { value: 'iOS & Android', label: 'App-Steuerung' },
  { value: 'Energie', label: 'Monitoring' },
];

const LoxoneSection = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-0.5 bg-[#6DAF3A]"></span>
              <span className="text-[#6DAF3A] text-xs font-semibold tracking-widest uppercase">Partner</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
              Loxone –<br />
              <span className="text-[#1E3A8A]">Intelligente Gebäudeautomation</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-6">
              Als Loxone Partner planen und realisieren wir intelligente Gebäudeautomationslösungen
              für Gewerbe- und Industriegebäude. Mit dem Loxone Miniserver als Herzstück werden
              Beleuchtung, Klima, Sicherheit und Energie in einem einzigen System vereint –
              einfach bedienbar, energieeffizient und zuverlässig.
            </p>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              Von der Planung über die Installation bis zur Programmierung und Inbetriebnahme –
              wir begleiten Ihr Loxone-Projekt von Anfang bis Ende und stehen auch danach
              als kompetenter Ansprechpartner zur Verfügung.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {highlights.map((h) => (
                <div key={h.label} className="bg-[#F8FAFC] border border-gray-200 rounded-lg px-4 py-3 text-center min-w-[110px]">
                  <div className="text-[#1E3A8A] font-bold text-sm">{h.value}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{h.label}</div>
                </div>
              ))}
            </div>
            <Link
              to="/anfrage"
              className="inline-flex items-center gap-2 bg-[#1E3A8A] hover:bg-[#1e40af] text-white font-semibold px-6 py-3 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              Loxone-Projekt anfragen
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
            </Link>
          </div>

          {/* Right: Logo + Image */}
          <div className="flex flex-col gap-5">
            <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center h-36">
              <img
                src="https://storage.readdy-site.link/project_files/0c485b57-fedb-40d8-8940-c50727e75c3c/cc570193-fe66-4bb7-9cb1-bc7bef89adab_loxo_partner.jpg?v=13213c94cd01057b5d8c1ee02d9584e3"
                alt="Loxone – Partner gowiTEC"
                className="max-h-full max-w-[260px] object-contain"
                loading="lazy"
              />
            </div>
            <div className="w-full h-[280px] rounded-xl overflow-hidden bg-gray-100">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20smart%20building%20automation%20control%20panel%20with%20touchscreen%20interface%20showing%20floor%20plan%20lighting%20and%20climate%20control%2C%20clean%20minimalist%20interior%20of%20a%20commercial%20office%20building%20with%20automated%20blinds%20and%20LED%20lighting%20system%2C%20professional%20installation%20with%20neat%20cable%20management&width=760&height=420&seq=loxone-building-auto-01&orientation=landscape"
                alt="Loxone Gebäudeautomation – gowiTEC Installation"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-14">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-gray-400 text-xs font-semibold tracking-widest uppercase whitespace-nowrap">Konkrete Einsatzbereiche</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        {/* Use Cases Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {useCases.map((uc) => (
            <div
              key={uc.title}
              className="bg-[#F8FAFC] rounded-xl border border-gray-100 p-7 flex flex-col gap-4 hover:border-[#6DAF3A]/40 transition-colors duration-200"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-[#F0F9E8] rounded-xl shrink-0">
                <i className={`${uc.icon} text-[#6DAF3A] text-xl`}></i>
              </div>
              <div>
                <h3 className="text-gray-900 font-bold text-base mb-2">{uc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{uc.description}</p>
              </div>
              <div className="flex flex-wrap gap-2 mt-auto pt-2">
                {uc.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-[#F0F9E8] text-[#4a8a20] text-xs font-medium px-2.5 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Banner */}
        <div className="bg-[#2d4a1a] rounded-xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="w-14 h-14 flex items-center justify-center bg-white/10 rounded-xl shrink-0">
              <i className="ri-home-smile-line text-white text-2xl"></i>
            </div>
            <div>
              <div className="text-white font-bold text-lg mb-1">Loxone Partner</div>
              <div className="text-green-200 text-sm leading-relaxed max-w-lg">
                Intelligente Gebäudeautomation für Gewerbe und Industrie – von der Planung
                über die Installation bis zur vollständigen Inbetriebnahme und Betreuung.
              </div>
            </div>
          </div>
          <Link
            to="/anfrage?leistung=Geb%C3%A4udeautomation+%26+Loxone"
            className="inline-flex items-center gap-2 bg-[#6DAF3A] hover:bg-[#5a9430] text-white font-semibold px-6 py-3 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm shrink-0"
          >
            Jetzt anfragen
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line"></i></span>
          </Link>
        </div>

      </div>
    </section>
  );
};

export default LoxoneSection;
